import { useState, useRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { preventEnterSubmit } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance & Banking',
  'Education',
  'Real Estate',
  'Legal',
  'Marketing & Advertising',
  'Consulting',
  'Manufacturing',
  'Retail & E-Commerce',
  'Hospitality & Tourism',
  'Non-Profit',
  'Government',
  'Media & Entertainment',
  'Agriculture',
  'Energy',
  'Transportation & Logistics',
  'Construction',
  'Professional Services',
  'Insurance',
  'Telecommunications',
  'Pharmaceutical',
  'Aerospace & Defense',
  'Automotive',
];

const steps = [
  {
    title: "Let's customize your CRM",
    description: "We'd love to help you get setup for success",
  },
  {
    title: "Tell us about your company",
    description: "This helps us customize your experience",
  },
  {
    title: "Set up your workspace",
    description: "Create a space for your team to collaborate",
  },
  {
    title: "Invite your team",
    description: "Work better together with your colleagues",
  },
  {
    title: "You're all set!",
    description: "Let's get started with your new workspace",
  },
];

const workspaceSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  type: z.string().min(1, "Industry is required"),
  size: z.string().min(1, "Company size is required"),
  teamSize: z.string().min(1, "Team size is required"),
});

interface WorkspaceOnboardingProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkspaceOnboarding({ open, onOpenChange }: WorkspaceOnboardingProps) {
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [industryOpen, setIndustryOpen] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const industryInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof workspaceSchema>>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: {
      name: '',
      type: '',
      size: '',
      teamSize: '',
    },
  });

  const filteredIndustries = industrySearch
    ? INDUSTRIES.filter(i => i.toLowerCase().includes(industrySearch.toLowerCase()))
    : INDUSTRIES;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        industryInputRef.current &&
        !industryInputRef.current.contains(e.target as Node)
      ) {
        setIndustryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSubmit = async (data: z.infer<typeof workspaceSchema>) => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Create new workspace
      const { data: workspace, error: workspaceError } = await supabase
        .from('workspaces')
        .insert({
          name: data.name,
          created_by: user.id,
          company_type: data.type,
          company_size: data.size,
          team_size: data.teamSize,
          is_personal: false,
        })
        .select()
        .single();

      if (workspaceError) throw workspaceError;

      // Add creator as admin
      const { error: memberError } = await supabase
        .from('workspace_members')
        .insert({
          workspace_id: workspace.id,
          user_id: user.id,
          role: 'admin',
          status: 'active',
        });

      if (memberError) throw memberError;

      toast({
        description: "Workspace created successfully",
      });

      // Store workspace ID and force full page navigation
      // Using window.location.href instead of navigate() + reload() to avoid
      // React state updates (dialog close, router) interfering with the reload
      localStorage.setItem('currentWorkspaceId', workspace.id);
      window.location.href = '/contacts';
    } catch (error) {
      console.error('Error creating workspace:', error);
      captureError(error as Error, { feature: 'workspace-workspace-onboarding-on-submit' });
      toast({
        title: "Error",
        description: "Failed to create workspace",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{steps[step].title}</DialogTitle>
          <DialogDescription>{steps[step].description}</DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">
              We'll guide you through setting up your workspace and help you get started with the features that matter most to your business.
            </p>
            <div className="flex justify-end">
              <Button onClick={nextStep}>Get Started</Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What is your company name?</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What industry are you in?</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          ref={industryInputRef}
                          placeholder="Type to search industries..."
                          value={industrySearch || field.value}
                          onChange={(e) => {
                            setIndustrySearch(e.target.value);
                            field.onChange(e.target.value);
                            setIndustryOpen(true);
                          }}
                          onFocus={() => setIndustryOpen(true)}
                          autoComplete="off"
                        />
                        {industryOpen && filteredIndustries.length > 0 && (
                          <div
                            ref={dropdownRef}
                            className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg"
                          >
                            {filteredIndustries.map((industry) => (
                              <button
                                key={industry}
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-default"
                                onClick={() => {
                                  field.onChange(industry);
                                  setIndustrySearch('');
                                  setIndustryOpen(false);
                                }}
                              >
                                {industry}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What is your company size?</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select company size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Just me</SelectItem>
                        <SelectItem value="2-5">2-5 employees</SelectItem>
                        <SelectItem value="5-10">5-10 employees</SelectItem>
                        <SelectItem value="10-50">10-50 employees</SelectItem>
                        <SelectItem value="50-100">50-100 employees</SelectItem>
                        <SelectItem value="101-200">101-200 employees</SelectItem>
                        <SelectItem value="200+">200+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="teamSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>How many teammates will be using the CRM?</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select team size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Just me</SelectItem>
                        <SelectItem value="2-5">2-5 teammates</SelectItem>
                        <SelectItem value="6-10">6-10 teammates</SelectItem>
                        <SelectItem value="11-25">11-25 teammates</SelectItem>
                        <SelectItem value="26+">26+ teammates</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button variant="outline" onClick={prevStep}>
                  Back
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Creating..." : "Create Workspace"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}

        {/* Progress indicators */}
        <div className="flex justify-center gap-2 mt-4">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-2 rounded-full transition-colors ${
                index === step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
